import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@workspace/ui/components/alert-dialog'
import { cn } from '@workspace/ui/lib/utils'
import { Eye, EyeOff, Loader2, Sparkles } from 'lucide-react'
import { useState } from 'react'
import { useSetStealthChoiceMutation } from '../../../hooks/api'

interface StealthChoiceGateProps {
  startupId: string
  startupName: string
}

export function StealthChoiceGate({ startupId, startupName }: StealthChoiceGateProps) {
  const mutation = useSetStealthChoiceMutation(startupId)
  const [pendingChoice, setPendingChoice] = useState<'visible' | 'stealth' | null>(null)

  const choose = (isStealth: boolean) => {
    setPendingChoice(isStealth ? 'stealth' : 'visible')
    mutation.mutate(
      { path: { startupId }, body: { isStealth } },
      { onError: () => setPendingChoice(null) }
    )
  }

  const isPending = mutation.isPending

  return (
    <AlertDialog open onOpenChange={() => {}}>
      <AlertDialogContent className="sm:max-w-xl">
        <AlertDialogHeader>
          <div className="flex items-start gap-3">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-lg border border-primary/40 bg-primary/10">
              <Sparkles className="size-5 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                Investor visibility
              </p>
              <AlertDialogTitle className="text-xl leading-tight">
                How should {startupName} appear to investors?
              </AlertDialogTitle>
            </div>
          </div>
          <AlertDialogDescription className="pt-3 text-sm leading-relaxed text-muted-foreground">
            We need your explicit consent before showing your startup to investors in our deal-flow.
            Pick one to continue. You can change this anytime in startup settings.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="grid gap-2 py-1">
          <ChoiceButton
            icon={<Eye className="size-4 text-emerald-400" />}
            title="Show to investors"
            description="Appear in matching investor deal-flow"
            tone="visible"
            pending={pendingChoice === 'visible'}
            disabled={isPending}
            onClick={() => choose(false)}
          />
          <ChoiceButton
            icon={<EyeOff className="size-4 text-muted-foreground" />}
            title="Keep in stealth mode"
            description="Hidden from investors until you change this"
            tone="stealth"
            pending={pendingChoice === 'stealth'}
            disabled={isPending}
            onClick={() => choose(true)}
          />
        </div>
      </AlertDialogContent>
    </AlertDialog>
  )
}

interface ChoiceButtonProps {
  icon: React.ReactNode
  title: string
  description: string
  tone: 'visible' | 'stealth'
  pending: boolean
  disabled: boolean
  onClick: () => void
}

function ChoiceButton({
  icon,
  title,
  description,
  tone,
  pending,
  disabled,
  onClick,
}: ChoiceButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex items-center gap-3 rounded-lg border border-border bg-card/60 px-4 py-3 text-left transition-colors',
        'hover:border-primary/40 hover:bg-primary/5',
        tone === 'visible' && 'hover:border-emerald-500/40 hover:bg-emerald-500/5',
        'disabled:cursor-not-allowed disabled:opacity-60'
      )}
    >
      <div className="flex size-8 shrink-0 items-center justify-center rounded-md border border-border bg-background">
        {pending ? <Loader2 className="size-4 animate-spin text-muted-foreground" /> : icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
    </button>
  )
}
